import { ChartPanelStatus } from 'components/charts/chart-panel-status'
// Trends & breakdowns — Figma "Frame 2087325084" (node 6202:124694).
//
// One report section (ReportSection tray + card) holding three panels split by
// vertical dividers. Each panel is CONFIG-DRIVEN from the per-slot chart config
// in the store: the chart TYPE picks the renderer (Bar / Stacked bar / Pie, all
// in the bespoke DS look), and metric / dimension / stack-by / stretch drive the
// data. At factory config the IP (Bar) and Veracity (Pie) panels show their
// hand-tuned datasets; changing any dropdown re-derives the breakdown (same
// deterministic engine the drawer preview uses).
//
// Bar + stacked hover tooltips are a dark floating card (DS rule: dark surface on
// light mode, light on dark), pinned to the cursor with no arrow. The donut keeps
// its built-in Gauge tooltip.
import {
	Suspense,
	lazy,
	useEffect,
	useMemo,
	useState,
	type HTMLAttributes,
	type ReactNode
} from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Divider } from '@clickguard/design-system'
import {
	Globe01,
	ShieldTick,
	CursorClick02,
	SlashCircle01,
	AlertTriangle,
	Lock01,
	CurrencyDollarCircle,
	BarChartSquare02
} from './icons'
import {
	FloatingTip,
	ChartBody,
	type TipState,
	type TipHandlers
} from './presentational/analytics-chart'
import { useAssetStore } from 'store'
import { chartFormatForMetric } from '../data/format-metric'
import { chartTitlesSnapshot } from './pdf/chart-title-snapshot'
import { ReportSection } from './report-section'
import { CHART_METRICS } from './chart-metrics'
// Lazy so the drawer's recharts dependency (previews only — the live charts use
// nivo) stays off this eagerly-loaded page's main bundle.
const ChartCustomizationDrawer = lazy(() =>
	import('./chart-customization-drawer').then((m) => ({
		default: m.ChartCustomizationDrawer
	}))
)
import {
	useReportLayoutStore,
	DEFAULT_CHART_CONFIG,
	resolveStackBy,
	type ChartCfg
} from '../store/use-report-layout-store'
import { useAnalyticsMeta, useBreakdown } from '../data/use-analytics-data'
import {
	breakdownToCats,
	breakdownToStack,
	dimensionIdForLabel,
	metricIdForLabel,
	panelTitle,
	panelTitleText,
	type PanelTitle
} from '../data/chart-adapters'
import { useDragReorder } from './use-drag-reorder'
import { DragGrip } from 'components/data-table-v2'
import { SlotToolGroup } from './slot-tool-group'
import { CustomizeReveal, usePrefersReducedMotion } from './customize-reveal'
import './customize.css'

// ── Config-driven data derivation ───────────────────────────────────────────
// `Cat` / `StackData` now live in the presentational chart module; re-exported
// here so existing importers of `./charts-row` still resolve them.
export type { Cat, StackData } from './presentational/analytics-chart'

// `icon` is the category glyph normally, or the drag grip in Customize mode
// (the grip replaces the icon — Figma 6348:133579, not shown alongside it).
const PanelHead = ({
	icon,
	title,
	tools,
	editMode,
	revealDelay = 0
}: {
	icon: ReactNode
	/** Two halves: the metric on top, the variables ("by … (Top 10) and …") under it. */
	title: PanelTitle
	tools?: ReactNode
	editMode?: boolean
	revealDelay?: number
}) => {
	const reduced = usePrefersReducedMotion()
	const titleKey = `${title.metric}|${title.by}`
	const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]
	const lift = {
		initial: reduced ? false : { opacity: 0, y: 5 },
		animate: { opacity: 1, y: 0 },
		exit: reduced ? { opacity: 0 } : { opacity: 0, y: -5 },
		transition: reduced ? { duration: 0 } : { duration: 0.16, ease }
	}
	return (
		// data-pdf-hide: the PDF renders panel titles as real text above each chart
		// shot, so the in-shot header is hidden during capture (customize.css).
		// Row 1 carries the metric and the tool group; row 2 gets the whole card
		// width for the variable line, which can be long ("by Country (Top 10) and
		// Campaign (Top 10)") and must not share a row with the four-segment tools.
		// mb-3: the Panel's own gap puts 16px between this head and the chart, but
		// the top axis tick overhangs the plot box, so the head clears real ink by
		// only ~6px — 12px more keeps it from reading crowded (prototype ab13650b8).
		<div data-pdf-hide className="mb-3 flex flex-col">
			{/* Reserve the tool-group height (26px = 24px box + 1px borders) in both
			    modes so entering Customize — which swaps the right slot in — doesn't
			    shift the row height. */}
			<div className="flex min-h-[26px] items-center justify-between gap-2">
				<div className="flex min-w-0 items-center gap-1.5">
					{/* Icon slot is only the drag grip in Customize mode now — the category
				    glyph was dropped from the header. Render nothing when there's no icon
				    so the title doesn't carry an empty leading gutter. */}
					{icon && (
						<span className="flex h-3 w-3 shrink-0 items-center justify-center text-[rgb(var(--cg-text-secondary))] [&>svg]:h-3 [&>svg]:w-3">
							{icon}
						</span>
					)}
					{/* Crossfade the title when the chart's metric / type / dimensions
				    change so the new name lifts in instead of snapping. Both lines share
				    one key, so the metric never crossfades against a stale variable line. */}
					<AnimatePresence mode="wait" initial={false}>
						<motion.span
							key={titleKey}
							title={title.metric}
							// 16px/24px semibold over a 14px/20px secondary line — the header
							// hierarchy from the prototype (Esteban 2026-08-27: "that header
							// title could be bigger as well, even both lines").
							className="min-w-0 truncate text-base font-semibold leading-6 text-gray-900"
							{...lift}
						>
							{title.metric}
						</motion.span>
					</AnimatePresence>
				</div>
				{/* In Customize mode the right slot becomes the panel tool group, which
			    animates in on enter and out on "Done" (CustomizeReveal keeps it
			    mounted through its exit). */}
				<CustomizeReveal show={!!editMode} delay={revealDelay}>
					{tools}
				</CustomizeReveal>
			</div>
			<AnimatePresence mode="wait" initial={false}>
				<motion.span
					key={titleKey}
					title={title.by}
					className={`block truncate text-sm leading-5 text-[rgb(var(--cg-text-secondary))] ${
						icon ? 'pl-[18px]' : ''
					}`}
					{...lift}
				>
					{title.by}
				</motion.span>
			</AnimatePresence>
		</div>
	)
}

const Panel = ({
	children,
	dropActive,
	grabbable,
	settling,
	highlight,
	corner,
	...rest
}: {
	children: ReactNode
	dropActive?: boolean
	grabbable?: boolean
	settling?: boolean
	highlight?: boolean
	// `rounded-l-xl` / `rounded-r-xl` on the first / last panel so the highlight ring
	// traces the card's 12px outer corners instead of poking out square.
	corner?: string
} & HTMLAttributes<HTMLDivElement>) => (
	<div
		{...rest}
		className={`flex min-w-0 flex-1 flex-col gap-4 p-4 transition-colors ${
			corner ?? ''
		} ${grabbable ? 'cursor-grab active:cursor-grabbing' : ''} ${
			dropActive ? 'bg-[#f4f3ff]' : ''
		} ${settling ? 'cg-drop-pop' : ''} ${highlight ? 'cg-card-highlight' : ''}`}
	>
		{children}
	</div>
)

// A panel is "a metric broken down by a dimension", so its header derives from
// the live config: "{Metric} by {Dimension}" with the metric's icon. Both follow
// the gear picker — no more stale hardcoded titles (the icon falls back to the
// globe for any metric outside CHART_METRICS).
const CHART_METRIC_ICONS: Record<string, ReactNode> = {
	'Ad clicks analyzed': <CursorClick02 />,
	'Valid clicks': <ShieldTick />,
	'Suspicious clicks': <AlertTriangle />,
	'Bad clicks': <SlashCircle01 />,
	'Bad traffic rate': <AlertTriangle />,
	'Blocked sources': <Lock01 />,
	'Prevented ad waste': <CurrencyDollarCircle />
}
// The title itself ("{Metric}" over "by {Dimension} (Top 10) and {Stack by}") is
// built by `panelTitle` in chart-adapters from the config AND the live breakdown,
// because the "(Top 10)" suffix depends on how many categories the data held.
const panelIcon = (cfg: ChartCfg): ReactNode =>
	CHART_METRIC_ICONS[cfg.metric] ?? <Globe01 />

// Shown when every panel is hidden (all three toggled off in the customize
// drawer). Mirrors the Key-metrics empty state verbatim — soft layered brand
// badge + title + muted export note — so both sections read the same when empty.
const ChartsEmptyState = ({ reduced }: { reduced: boolean }) => (
	<motion.div
		initial={reduced ? false : { opacity: 0, y: 8 }}
		animate={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
		className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center"
	>
		<div className="relative flex h-16 w-16 items-center justify-center">
			<motion.span
				aria-hidden
				className="absolute inset-0 rounded-2xl bg-[rgba(85,75,253,0.06)]"
				animate={
					reduced
						? undefined
						: { opacity: [0.5, 1, 0.5], scale: [0.96, 1, 0.96] }
				}
				transition={
					reduced
						? undefined
						: { duration: 3, ease: 'easeInOut', repeat: Infinity }
				}
			/>
			<span
				aria-hidden
				className="absolute inset-[10px] rounded-xl bg-[rgba(85,75,253,0.1)]"
			/>
			<span className="relative flex h-9 w-9 items-center justify-center rounded-[10px] bg-white shadow-sm ring-1 ring-[#ececf5]">
				<BarChartSquare02 className="h-4 w-4 text-[rgb(var(--cg-brand))]" />
			</span>
		</div>
		<div className="space-y-1">
			<p className="text-[15px] font-semibold text-[rgb(var(--cg-text-primary))]">
				No charts selected
			</p>
			<p className="mx-auto max-w-[320px] text-[13px] leading-5 text-gray-500">
				This section will be hidden when the report is exported.
			</p>
		</div>
	</motion.div>
)

// Stable panel order ids (the store reorders these; content lives in the map).
const PANEL_IDS = ['ip', 'veracity', 'time'] as const

interface ChartPanel {
	cfg: ChartCfg
	icon: ReactNode
	title: PanelTitle
	body: ReactNode
}

// Everything one chart position needs: its resolved config (draft → defaults,
// stack-by never equal to the primary), the live breakdown for the metric × the
// primary dimension (× the stack-by for a Stacked bar), the panel's formatter
// (metric registry format + asset currency, so axis / bars / legend / tooltip
// agree on the unit), and the rendered body — a dotted loader while the query loads,
// a no-data state when it resolves empty (or errors), else the live chart.
const useChartPanel = (
	id: (typeof PANEL_IDS)[number],
	chartConfig: Record<string, ChartCfg>,
	onTip: TipHandlers['onTip'],
	offTip: TipHandlers['offTip']
): ChartPanel => {
	const raw = chartConfig[id] ?? DEFAULT_CHART_CONFIG[id]
	const cfg: ChartCfg = {
		...raw,
		dimension2: resolveStackBy(raw.dimension, raw.dimension2)
	}
	const metricId = metricIdForLabel(cfg.metric)
	const dimensionId = dimensionIdForLabel(cfg.dimension)
	const stacked = cfg.chartType === 'Stacked bar'
	const stackById = stacked ? dimensionIdForLabel(cfg.dimension2) : undefined
	const q = useBreakdown(metricId, dimensionId, stackById)
	const meta = useAnalyticsMeta().data
	const currency = useAssetStore((s) => s.asset?.currency) || 'USD'
	const format = useMemo(
		() =>
			chartFormatForMetric(
				metricId,
				currency,
				meta?.metrics.find((m) => m.id === metricId)?.format
			),
		[metricId, currency, meta]
	)
	const cats = useMemo(
		() => breakdownToCats(q.data, dimensionId, cfg.chartType),
		[q.data, dimensionId, cfg.chartType]
	)
	const stack = useMemo(
		() => breakdownToStack(q.data, dimensionId, stackById),
		[q.data, dimensionId, stackById]
	)
	const empty = stacked ? stack.cols.length === 0 : cats.length === 0
	const body =
		q.isLoading && !q.data ? (
			<ChartPanelStatus mode="loading" />
		) : q.isError || empty ? (
			<ChartPanelStatus mode="empty" />
		) : (
			<ChartBody
				cfg={cfg}
				cats={cats}
				stack={stack}
				format={format}
				onTip={onTip}
				offTip={offTip}
			/>
		)
	return { cfg, icon: panelIcon(cfg), title: panelTitle(cfg, q.data), body }
}

export const ChartsRow = () => {
	const reduced = usePrefersReducedMotion()
	const [tip, setTip] = useState<TipState | null>(null)
	// "Chart block customization" drawer — opened by the tray pencil (Figma 6561:211492).
	const [customizeOpen, setCustomizeOpen] = useState(false)
	const editMode = useReportLayoutStore((s) => s.editMode)
	const chartOrder = useReportLayoutStore((s) => s.chartOrder)
	const hiddenCharts = useReportLayoutStore((s) => s.hiddenCharts)
	const chartConfig = useReportLayoutStore((s) => s.chartConfig)
	const sectionName = useReportLayoutStore((s) => s.sectionNames.chart)
	const setSectionName = useReportLayoutStore((s) => s.setSectionName)
	const initChartOrder = useReportLayoutStore((s) => s.initChartOrder)
	const reorderChart = useReportLayoutStore((s) => s.reorderChart)
	const setChartCfg = useReportLayoutStore((s) => s.setChartCfg)

	const onTip: TipHandlers['onTip'] = (e, content) =>
		setTip({ ...content, x: e.clientX, y: e.clientY })
	const offTip = () => setTip(null)

	// Seed/reconcile the order with the panel id set. Re-run when entering Customize:
	// `enterEdit` reseeds the draft from `committed`, whose order array is empty
	// until the user has saved a custom order — leaving the draft order empty would
	// make reorderChart a no-op (arrayMove on []), so the cards wouldn't move. This
	// repopulates it to PANEL_IDS (reconcile preserves any saved order) on edit.
	useEffect(() => {
		initChartOrder([...PANEL_IDS])
	}, [initChartOrder, editMode])

	const dnd = useDragReorder(reorderChart)

	// One hook per panel position resolves its config and data; the chart TYPE in
	// the config decides which body renders.
	const ip = useChartPanel('ip', chartConfig, onTip, offTip)
	const veracity = useChartPanel('veracity', chartConfig, onTip, offTip)
	const time = useChartPanel('time', chartConfig, onTip, offTip)
	const PANELS: Record<(typeof PANEL_IDS)[number], ChartPanel> = {
		ip,
		veracity,
		time
	}

	// Publish the live titles for the PDF builder (the data-driven "(Top 10)"
	// lives only here). An effect, not a render-time write, keyed on the titles.
	const titles = PANEL_IDS.map((id) => panelTitleText(PANELS[id].title))
	useEffect(() => {
		chartTitlesSnapshot.current = Object.fromEntries(
			PANEL_IDS.map((id, i) => [id, titles[i]])
		)
	}, titles)

	// Full saved order (fall back to the natural order before init lands), then the
	// rendered subset with hidden panels removed so the rest fill the container.
	// Drag handlers use each panel's index in the FULL order so reordering stays
	// correct even while some panels are hidden.
	const fullOrder = (chartOrder.length ? chartOrder : [...PANEL_IDS]).filter(
		(id): id is (typeof PANEL_IDS)[number] => id in PANELS
	)
	const order = fullOrder.filter((id) => !hiddenCharts.includes(id))

	return (
		<>
			<ReportSection
				title={sectionName}
				onTitleChange={(next) => setSectionName('chart', next)}
				onCustomize={() => setCustomizeOpen(true)}
			>
				<FloatingTip tip={tip} />
				{order.length === 0 ? (
					<ChartsEmptyState reduced={reduced} />
				) : (
					/* AnimatePresence so hiding/showing a panel via `hiddenCharts`
				    animates its width+opacity (and its leading divider) in/out instead
				    of snapping the survivors to reflow width. `layout="position"` on each
				    wrapper slides panels to new slots on drag-reorder (position-only, so
				    text isn't scaled/crushed); omitted when reduced. */
					<div className="flex items-stretch">
						<AnimatePresence initial={false}>
							{order.map((pid, vi) => {
								const p = PANELS[pid]
								const i = fullOrder.indexOf(pid)
								return (
									<motion.div
										key={pid}
										className="flex min-w-0 items-stretch"
										{...(reduced ? {} : { layout: 'position' as const })}
										initial={
											reduced
												? { opacity: 0 }
												: { opacity: 0, flexGrow: 0, flexBasis: 0 }
										}
										animate={
											reduced
												? { opacity: 1 }
												: { opacity: 1, flexGrow: 1, flexBasis: '0%' }
										}
										exit={
											reduced
												? { opacity: 0 }
												: { opacity: 0, flexGrow: 0, flexBasis: 0 }
										}
										transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
										style={{ flexGrow: 1, flexBasis: '0%', overflow: 'hidden' }}
									>
										{/* Leading divider rides INSIDE the collapsing wrapper so when a
									    panel exits its divider collapses with it — no orphaned gap.
									    First VISIBLE panel gets none. */}
										{vi > 0 && <Divider orientation="vertical" />}
										<Panel
											{...(editMode
												? { ...dnd.gripProps(i), ...dnd.zoneProps(i) }
												: {})}
											data-card-id={pid}
											grabbable={editMode}
											dropActive={
												editMode &&
												dnd.overIndex === i &&
												dnd.dragIndex !== null &&
												dnd.dragIndex !== i
											}
											settling={dnd.justMoved === i}
											corner={`transition-[border-radius] duration-300 ${
												vi === 0
													? 'rounded-l-xl'
													: vi === order.length - 1
													? 'rounded-r-xl'
													: ''
											}`}
										>
											<PanelHead
												icon={editMode ? <DragGrip /> : undefined}
												title={p.title}
												editMode={editMode}
												revealDelay={i * 45}
												tools={
													<SlotToolGroup
														selectedChartType={p.cfg.chartType}
														selectedMetric={p.cfg.metric}
														selectedDimension={p.cfg.dimension}
														selectedDimension2={p.cfg.dimension2}
														showDimensions
														metricOptions={CHART_METRICS}
														// Each panel's metric is independent — changing one never
														// touches the others, and the same metric may appear on more
														// than one panel.
														onSelectMetric={(metric) => {
															if (metric === p.cfg.metric) return
															setChartCfg(pid, { metric })
														}}
														onSelectChartType={(chartType) =>
															setChartCfg(pid, { chartType })
														}
														onSelectDimension={(dimension) => {
															if (dimension === p.cfg.dimension) return
															setChartCfg(pid, { dimension })
														}}
														onSelectDimension2={(dimension2) => {
															if (dimension2 === p.cfg.dimension2) return
															setChartCfg(pid, { dimension2 })
														}}
													/>
												}
											/>
											{/* Swap the chart body on a chart-TYPE or DIMENSION change: the old
										    shape scales + fades out and the new one scales + fades in
										    (framer mode="wait"), so a type switch reads as a clear transition
										    rather than a snap. A metric-only change keeps the same key, so the
										    body stays mounted and its bars/segments tween height instead
										    (.pa-bar-grow / .pa-stack-*). */}
											<AnimatePresence mode="wait" initial={false}>
												<motion.div
													key={`${p.cfg.chartType}|${p.cfg.dimension}|${
														p.cfg.chartType === 'Stacked bar'
															? p.cfg.dimension2
															: ''
													}`}
													className="flex min-h-0 flex-1 flex-col gap-4"
													initial={
														reduced ? false : { opacity: 0, scale: 0.96 }
													}
													animate={{ opacity: 1, scale: 1 }}
													exit={
														reduced
															? { opacity: 0 }
															: { opacity: 0, scale: 0.96 }
													}
													transition={
														reduced
															? { duration: 0 }
															: { duration: 0.22, ease: [0.16, 1, 0.3, 1] }
													}
												>
													{p.body}
												</motion.div>
											</AnimatePresence>
										</Panel>
									</motion.div>
								)
							})}
						</AnimatePresence>
					</div>
				)}
			</ReportSection>
			<Suspense fallback={null}>
				<ChartCustomizationDrawer
					open={customizeOpen}
					onClose={() => setCustomizeOpen(false)}
				/>
			</Suspense>
		</>
	)
}
